import clsx from 'clsx';
import { ReactComponent as GiftIcon } from 'img/icons/onboarding/gift_outline_20.svg';
import img1 from 'img/onboarding/image-1.png';
import img2 from 'img/onboarding/image-2.png';
import img3 from 'img/onboarding/image-3.png';
import img4 from 'img/onboarding/image-4.png';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onboardingStepsEnabled, onboardingStepsSettings } from 'reactApp/appHelpers/featuresHelpers/features/onboardingSteps';
import { setItemFromLocalStorage } from 'reactApp/appHelpers/localstorageHelpers';
import {
    AnalyticButtonName,
    AnyliticsActions,
    AnyliticsCategories,
    COMPLETION_STATE,
    ONBOARDING_STATE_KEY,
} from 'reactApp/modules/onboardingSteps/onboardingSteps.constants';
import {
    closeShareHint,
    collapseOnboardingPanel,
    expandOnboardingPanel,
    hideOnboardingPanel,
} from 'reactApp/modules/onboardingSteps/onboardingSteps.module';
import {
    getOnboardingPanelOpen,
    getOnboardingPanelVisible,
    getOnboardingShareHintItemId,
    getOnboardingStepIndex,
} from 'reactApp/modules/onboardingSteps/onboardingSteps.selector';
import { Step as StepEnum, StepName } from 'reactApp/modules/onboardingSteps/onboardingSteps.types';
import { loadProductsAndOpenFastCheckout } from 'reactApp/modules/payment/payment.module';
import { getProductById } from 'reactApp/modules/products/products.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { closeOnboardingQRModal, openOnboardingQRModal } from 'reactApp/ui/OnboardingQRModal/OnboardingQRModal.helpers';
import { DropdownOutlineIcon } from 'reactApp/ui/VKUIIcons';
import { sendDwh } from 'reactApp/utils/ga';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import s from './OnboardingPanel.css';
import { Step } from './Step';

const PUBLIC_TIP = {
    text: (
        <>
            Делись файлами <br /> по ссылке — это удобно
        </>
    ),
    qaId: 'onboarding-tip',
};

const tariff = onboardingStepsSettings?.tariff;

export const OnboardingPanel = () => {
    const [tooltipTarget, setTooltipTarget] = useState<Element | null>(null);
    const currentIndex = useSelector(getOnboardingStepIndex);
    const isOpened = useSelector(getOnboardingPanelOpen);
    const isVisible = useSelector(getOnboardingPanelVisible);
    const hintId = useSelector(getOnboardingShareHintItemId);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const dispatch = useDispatch();
    const isDark = isDarkThemeModeEnabled();

    const productId = tariff;
    const product: Product | undefined = useSelector((state: RootState) => getProductById(state, productId));
    const space = product?.space?.space;
    const units = product?.space?.units;

    const handleClick = () => {
        if (isOpened) {
            dispatch(collapseOnboardingPanel());
        } else {
            dispatch(expandOnboardingPanel());
        }
    };

    useEffect(() => {
        if (hintId) {
            const target = document.querySelector(`[data-item-id="${hintId}"]`);

            setTooltipTarget(target);
        }
    }, [hintId]);

    const handleHintClose = useCallback(() => {
        dispatch(closeShareHint());
        setTooltipTarget(null);
    }, []);

    const onCloseQR = useCallback(() => {
        dispatch(closeOnboardingQRModal());
    }, []);

    const handleQRCodeOpen = useCallback(() => {
        openOnboardingQRModal({ onClose: onCloseQR });
        sendDwh({
            eventCategory: AnyliticsCategories.STEPS,
            action: AnyliticsActions.CLICK,
            dwhData: {
                step: currentIndex,
                step_name: StepName[currentIndex],
                button_name: AnalyticButtonName.DOWNLOAD,
                paid_user: isPaidUser,
            },
        });
    }, [currentIndex, isPaidUser]);

    const handleTariffOpen = useCallback(() => {
        sendDwh({
            eventCategory: AnyliticsCategories.STEPS,
            action: AnyliticsActions.CLICK,
            dwhData: {
                step: currentIndex,
                step_name: StepName[currentIndex],
                button_name: AnalyticButtonName.GET_TRIAL,
                paid_user: isPaidUser,
            },
        });

        dispatch(
            loadProductsAndOpenFastCheckout({
                productId: tariff,
                paySource: onboardingStepsSettings?.paySourceId,

                onSuccess: () => {
                    dispatch(hideOnboardingPanel());
                    sendDwh({
                        eventCategory: AnyliticsCategories.STEPS,
                        action: AnyliticsActions.PAYMENT_SUCCESS,
                        dwhData: {
                            paySource: onboardingStepsSettings?.paySourceId,
                            quota: product?.space?.original,
                            period: product?.trialPeriod,
                            is_trial: product?.hasTrial,
                            tariff,
                        },
                    });
                    dispatch(setItemFromLocalStorage(ONBOARDING_STATE_KEY, COMPLETION_STATE));
                },
            })
        );
    }, [isPaidUser, currentIndex]);

    const steps = useMemo(
        () => [
            {
                position: 1,
                title: 'Загрузи первый файл',
                bgImg: img1,
            },
            {
                position: 2,
                title: 'Поделись ссылкой',
                bgImg: img2,
            },
            {
                position: 3,
                title: 'Скачай приложение',
                bgImg: img3,
                onClick: currentIndex === StepEnum.DOWNLOAD_APP ? handleQRCodeOpen : undefined,
            },
            {
                position: 4,
                title: `Забирай ${space} ${units}`,
                bgImg: img4,
                icon: <GiftIcon />,
                onClick: currentIndex === StepEnum.ACHIEVEMENT ? handleTariffOpen : undefined,
            },
        ],
        [handleQRCodeOpen, currentIndex, space, units]
    );

    const currentStep = steps[currentIndex];

    if (!onboardingStepsEnabled) {
        return null;
    }

    return (
        <>
            <div className={clsx(s.root, { [s.opened]: isOpened, [s.visible]: isVisible, [s.dark]: isDark })}>
                <div className={s.panel}>
                    <img className={s.bgImage} src={currentStep.bgImg} alt={currentStep.title} />
                    <div className={s.header} onClick={handleClick}>
                        {!isOpened && <span>Начало работы с Облаком</span>}
                        <span className={s.dropdownIcon}>
                            <DropdownOutlineIcon />
                        </span>
                    </div>
                    <div className={s.body}>
                        {!isOpened && (
                            <Step
                                title={currentStep.title}
                                icon={currentStep.icon}
                                position={currentStep.position}
                                onClick={currentStep.onClick}
                            />
                        )}
                        {isOpened && (
                            <ul className={s.steps}>
                                {steps.map((item, index) => (
                                    <Step
                                        key={index}
                                        title={item.title}
                                        icon={item.icon}
                                        position={item.position}
                                        tagName="li"
                                        completed={item.position < currentStep.position}
                                        onClick={item.onClick}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            {hintId && tooltipTarget && (
                <FloatingTooltip
                    target={tooltipTarget ?? null}
                    className={s.tooltip}
                    size={ETooltipSize.large}
                    onClose={handleHintClose}
                    text={PUBLIC_TIP.text}
                    placement={ETooltipPlacement.bottom}
                    qaId={PUBLIC_TIP.qaId}
                    usePortal
                />
            )}
        </>
    );
};
